import type { NomineeDetailResponse } from '@api/nominees-api';
import { updateNomineeParticipant } from '@api/nominees-api';
import ConfirmationModal from '@components/confirmation-modal';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { type EditNomineeFormData, editNomineeSchema } from '@validation/schemas';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { sileo } from 'sileo';

interface EditNomineeModalProps {
  isOpen: boolean;
  onClose: () => void;
  nominee: NomineeDetailResponse | null;
}

export default function EditNomineeModal({
  isOpen,
  onClose,
  nominee,
}: EditNomineeModalProps): React.JSX.Element {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<EditNomineeFormData>({
    resolver: zodResolver(editNomineeSchema),
    defaultValues: { name: '', surname: '', photoUrl: '', profileUrl: '', city: '' },
  });

  useEffect(() => {
    if (isOpen && nominee) {
      const { participant } = nominee;
      reset({
        name: participant.name,
        surname: participant.surname ?? '',
        photoUrl: participant.photoUrl ?? '',
        profileUrl: participant.profileUrl ?? '',
        city: participant.city ?? '',
      });
    }
  }, [isOpen, nominee, reset]);

  if (!nominee) return <></>;

  const { participant } = nominee;

  const onValid = (): void => {
    setIsConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    const data = getValues();

    await updateNomineeParticipant(participant.id, {
      name: data.name,
      surname: data.surname || null,
      photoUrl: data.photoUrl || null,
      profileUrl: data.profileUrl || null,
      city: data.city || null,
    });

    sileo.success({ title: 'Nominee updated successfully' });
    setIsConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit nominee"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSubmit(onValid)()}
            >
              <Save size={16} />
              Save
            </button>
          </>
        }
      >
        <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
          <div className="modal-form__row">
            <div className="form-group">
              <label htmlFor="edit-nominee-name" className="form-label">
                Name
              </label>
              <input
                id="edit-nominee-name"
                type="text"
                className={`form-input${errors.name ? ' form-input--error' : ''}`}
                {...register('name')}
              />
              <FieldError error={errors.name} />
            </div>
            <div className="form-group">
              <label htmlFor="edit-nominee-surname" className="form-label">
                Surname
              </label>
              <input
                id="edit-nominee-surname"
                type="text"
                className={`form-input${errors.surname ? ' form-input--error' : ''}`}
                {...register('surname')}
              />
              <FieldError error={errors.surname} />
            </div>
          </div>

          <div className="form-group">
            <span className="form-label">Docplanner doctor ID</span>
            <span className="form-input form-input--readonly">{participant.dpDoctorId}</span>
          </div>

          <div className="form-group">
            <label htmlFor="edit-nominee-photo" className="form-label">
              Photo url
            </label>
            <input
              id="edit-nominee-photo"
              type="url"
              className={`form-input${errors.photoUrl ? ' form-input--error' : ''}`}
              {...register('photoUrl')}
            />
            <FieldError error={errors.photoUrl} />
          </div>

          <div className="form-group">
            <label htmlFor="edit-nominee-profile" className="form-label">
              Profile url
            </label>
            <input
              id="edit-nominee-profile"
              type="url"
              className={`form-input${errors.profileUrl ? ' form-input--error' : ''}`}
              {...register('profileUrl')}
            />
            <FieldError error={errors.profileUrl} />
          </div>

          <div className="form-group">
            <label htmlFor="edit-nominee-city" className="form-label">
              City
            </label>
            <input
              id="edit-nominee-city"
              type="text"
              className={`form-input${errors.city ? ' form-input--error' : ''}`}
              {...register('city')}
            />
            <FieldError error={errors.city} />
          </div>
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        title="Update nominee"
        message="Are you sure you want to save the changes to this nominee?"
        confirmLabel="Save"
      />
    </>
  );
}
